import { agencyStrings } from '@data/agency';
import { routePlanStrings } from '@data/routePlan';
import { vesselScheduleStrings } from '@data/vesselSchedule';
import { agency, agencyBase } from '@models/Agency';
import { Country } from '@models/Country';
import { baseInvoiceDetail } from '@models/Invoice';
import { InvoiceHelper } from '@models/next/InvoiceHelper';
import { warehouse, warehouseBaseWithoutAgency } from '@models/Warehouse';
import {
  foreignKeyField,
  formStrings,
  requiredNumberField,
  requiredPositiveNumberField,
  requiredStringField,
} from '@utils/forms';
import { ID } from '@utils/types';
import * as t from 'io-ts';
import { isEqual, sortBy, sum } from 'lodash-es';
import * as h from 'tyrann-io';

export const routePlanOrder = t.union([
  t.literal('RECOMMENDED'),
  t.literal('CHEAP'),
  t.literal('FAST'),
]);

export type RoutePlanOrder = t.TypeOf<typeof routePlanOrder>;

export const serviceName = t.union([
  t.literal('D2D'),
  t.literal('D2DSHENZHEN+'),
  t.literal('海上速達便'),
  t.literal('D2DSHENZHEN'),
  t.literal('D2DQINGDAO'),
]);

export type ServiceName = t.TypeOf<typeof serviceName>;

export const routePlanSchedule = t.type({
  warehouse_closing_date: t.number,
  etd: t.number,
  eta: t.number,
  pickup_date: t.number,
});

export type RoutePlanSchedule = t.TypeOf<typeof routePlanSchedule>;

export const baseRoutePlan = t.type({
  name: t.string,
  service_name: serviceName,
  receiving_city: t.string,
  pol: t.string,
  pod: t.string,
  schedules: h.omittable(t.array(routePlanSchedule)),
});

export type BaseRoutePlan = t.TypeOf<typeof baseRoutePlan>;

export const routePlan = t.type({
  ...baseRoutePlan.props,
  disabled: t.boolean,
  id: t.number,
  plan_id: t.number,
  option_id: t.string,
  agency,
  receiving_warehouses: t.array(warehouse),
  export_warehouse: warehouse,
  updated_at: t.string,
  created_at: t.string,
});

export type RoutePlan = t.TypeOf<typeof routePlan>;

export const bookingInquiryRoutePlan = t.type({
  ...baseRoutePlan.props,
  agency: agencyBase,
  receiving_warehouses: t.array(warehouseBaseWithoutAgency),
  id: t.number,
  plan_id: t.number,
  option_id: t.string,
  export_warehouse: warehouseBaseWithoutAgency,
});

export type BookingInquiryRoutePlan = t.TypeOf<typeof bookingInquiryRoutePlan>;

export const postRoutePlan = t.type({
  agency_id: t.number,
  export_warehouse_id: t.number,
  receiving_warehouses: t.array(t.number),
  disabled: t.boolean,
  ...baseRoutePlan.props,
});

export type PostRoutePlan = t.TypeOf<typeof postRoutePlan>;

export const patchRoutePlan = t.partial({
  ...routePlan.props,
  receiving_warehouses: t.array(t.number),
});

export type PatchRoutePlan = t.TypeOf<typeof patchRoutePlan>;

export const routePlanScheduleForm = t.type({
  warehouse_closing_date: requiredNumberField(routePlanStrings.warehouseClosingDate)
    .max(6, formStrings.invalid(routePlanStrings.warehouseClosingDate))
    .min(0, formStrings.invalid(routePlanStrings.warehouseClosingDate)),
  etd: requiredNumberField(routePlanStrings.etd)
    .max(6, formStrings.invalid(routePlanStrings.etd))
    .min(0, formStrings.invalid(routePlanStrings.etd)),
  eta: requiredNumberField(routePlanStrings.eta)
    .max(6, formStrings.invalid(routePlanStrings.eta))
    .min(0, formStrings.invalid(routePlanStrings.eta)),
  pickup_date: requiredNumberField(routePlanStrings.pickupDate)
    .max(6, formStrings.invalid(routePlanStrings.pickupDate))
    .min(0, formStrings.invalid(routePlanStrings.pickupDate)),
});

export type RoutePlanScheduleForm = t.TypeOf<typeof routePlanScheduleForm>;

export const routePlanSchedulesForm = t.type({
  schedules: h.array(routePlanScheduleForm),
});

export type RoutePlanSchedulesForm = t.TypeOf<typeof routePlanSchedulesForm>;

export const routePlanScheduleFormInitialValues: RoutePlanScheduleForm = {
  warehouse_closing_date: 0,
  etd: 0,
  eta: 0,
  pickup_date: 0,
};

export const routePlanForm = t.type({
  ...routePlanSchedulesForm.props,
  service_name: requiredStringField(routePlanStrings.serviceName),
  receiving_city: requiredStringField(routePlanStrings.receivingCity),
  pol: requiredStringField(vesselScheduleStrings.pol),
  pod: requiredStringField(vesselScheduleStrings.pod),
  agency_id: foreignKeyField(agencyStrings.agency, { required: true }),
  receiving_warehouses: h.array(
    foreignKeyField(agencyStrings.receivingWarehouse, { required: true }),
  ),
  export_warehouse_id: foreignKeyField(agencyStrings.exportWarehouse, { required: true }),
});

export type RoutePlanForm = t.TypeOf<typeof routePlanForm>;

export const routePlanFormInitialValues: RoutePlanForm = {
  schedules: [routePlanScheduleFormInitialValues],
  receiving_warehouses: [],
  service_name: '',
  receiving_city: '',
  pol: '',
  pod: '',
  agency_id: 0,
  export_warehouse_id: 0,
};

export const simulatorInvoiceDetail = t.type({
  ...baseInvoiceDetail.props,
  exchange_base_rate: t.number,
  exchange_base_date: t.string,
});

export type SimulatorInvoiceDetail = t.TypeOf<typeof simulatorInvoiceDetail>;

export const simulatorBaggage = t.type({
  total_kg_num: t.number,
  total_m3_num: t.number,
});

export type SimulatorBaggage = t.TypeOf<typeof simulatorBaggage>;

export const simulatorPickup = t.type({
  baggage: simulatorBaggage,
});

export type SimulatorPickup = t.TypeOf<typeof simulatorPickup>;

export const simulatorTruckOption = t.type({
  truck_type: t.string,
});

export type SimulatorTruckOption = t.TypeOf<typeof simulatorTruckOption>;

export const simulatorResultCities = t.type({
  receiver_city: t.string,
});

export interface SimulatorResultCities extends ID<t.TypeOf<typeof simulatorResultCities>> {}

export const simulatorResult = t.type({
  ...simulatorResultCities.props,
  delivery_days: t.number, // 納品日 - 搬入予定日・集荷日
  target_kg: t.number, // 基準計算重量
  simulator_delivery_days: t.number,
  simulator_pickup_days: t.number,
  receiving_warehouse_days: t.number,
  export_warehouse_days: t.number,
  sea_transportation_days: t.number,
  waiting_days: t.number,
  devan_days: t.number,
  warehouse_date: t.string,
  pol_date: t.string,
  pod_date: t.string,
  invoice_details: t.array(simulatorInvoiceDetail),
  pickups: t.array(simulatorPickup),
  baggage: simulatorBaggage,
  truck_options: t.array(simulatorTruckOption),
  grade: t.number,
});

export type SimulatorResult = t.TypeOf<typeof simulatorResult>;

export const getSimulatorResultTotalPrice = (v: SimulatorResult, country?: Country) => {
  return sum(
    v.invoice_details
      .filter((d) => country === undefined || d.payer_mark === country)
      .map(
        (d) =>
          InvoiceHelper.getInvoiceDetailsComputed(d).totalPriceForCurrency(
            d.exchange_base_rate,
            'JPY',
          ) ?? 0,
      ),
  );
};

export const routePlanSimulator = t.type({
  route_plan: routePlan,
  simulator: simulatorResult,
});

export type RoutePlanSimulator = t.TypeOf<typeof routePlanSimulator>;

export const baseRoutePlanSimulator = t.type({
  route_plan: baseRoutePlan,
  simulator: simulatorResult,
});

export type BaseRoutePlanSimulator = t.TypeOf<typeof baseRoutePlanSimulator>;

export const memberAgencyFormItem = t.type({
  plan_id: t.number,
  options: t.array(routePlan),
  route_plan_id: requiredPositiveNumberField(agencyStrings.agency),
});

export type MemberAgencyFormItem = t.TypeOf<typeof memberAgencyFormItem>;

export const memberAgencyForm = t.type({
  items: t.array(memberAgencyFormItem),
});

export type MemberAgencyForm = t.TypeOf<typeof memberAgencyForm>;

export const areMemberAgencyFormItemAgencyOptionsEqual = (
  a: MemberAgencyFormItem,
  b: MemberAgencyFormItem,
) => {
  return isEqual(
    sortBy(a.options.map((o) => o.agency.id)),
    sortBy(b.options.map((o) => o.agency.id)),
  );
};

export const routePlanQuery = t.partial({
  agency_id: t.array(h.number().cast()),
  company_id: t.number,
  ordering: t.array(t.string),
  pod: t.array(t.string),
  pol: t.array(t.string),
  service_name: t.array(t.string),
});

export type RoutePlanQuery = t.TypeOf<typeof routePlanQuery>;

export const routePlanQueryForm = t.partial({
  agency_id: h.array(h.number().cast()),
  ordering: h.array(h.string()),
  pod: h.array(h.string()),
  pol: h.array(h.string()),
  service_name: h.array(h.string()),
});

export type RoutePlanQueryForm = t.TypeOf<typeof routePlanQueryForm>;

export const mapRoutePlanToRoutePlanDaysForm = (r: RoutePlan): RoutePlanSchedulesForm => {
  return { schedules: r.schedules ?? [] };
};

export const mapRoutePlanToRoutePlanForm = (r: RoutePlan): RoutePlanForm => {
  return {
    ...mapRoutePlanToRoutePlanDaysForm(r),
    receiving_warehouses: r.receiving_warehouses.map((w) => w.id),
    service_name: r.service_name,
    receiving_city: r.receiving_city,
    pol: r.pol,
    pod: r.pod,
    agency_id: r.agency.id,
    export_warehouse_id: r.export_warehouse.id,
  };
};
